/** @jsx isolateComponent */
import { css } from '@emotion/react';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { Colors, Heading, Icon, Typography } from '@zapier/design-system';
import { convertHtmlAstToReactElements } from './utils';
import type { HTMLAST } from './utils';

type Props = {
  /**
   * The description of this Zap template as an HTML AST,
   * as provided by the server.
   */
  htmlAst: HTMLAST;
};

const Styles = labelStyles('ZapCardDetailsHtml', {
  root: css`
    ${Typography.paragraph3};
    color: ${Colors.gray80};

    // Can't use '> *:first-child' w/o emotion complaining...
    > h1:first-of-type,
    > h2:first-of-type,
    > h3:first-of-type,
    > h4:first-of-type,
    > h5:first-of-type,
    > h6:first-of-type,
    > p:first-of-type,
    > ol:first-of-type,
    > ul:first-of-type {
      margin-top: 0;
    }
  `,
  heading: css`
    ${Typography.smallPrint3Semibold};
    margin: 20px 0 5px;
    text-transform: uppercase;
  `,
  paragraph: css`
    margin: 10px 0;
  `,
  list: css`
    display: grid;
    grid-template-columns: 100%;
    gap: 5px;
  `,
  listItem: css`
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 15px;
    align-items: center;
    padding: 5px 15px;
    background-color: ${Colors.gray5};
    border-radius: 5px;
    color: ${Colors.gray};
    ${Typography.smallPrint1Semibold};
  `,
  listItemIcon: css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    background-color: ${Colors.white};
    border: 1px solid ${Colors.gray20};
    border-radius: 8px;
  `,
});

const ListItem = ({ children }: { children: React.ReactNode }) => {
  return (
    <li css={Styles.listItem}>
      <span css={Styles.listItemIcon}>
        <Icon color="gray80" name="formCheckCircle" size={15} />
      </span>
      <div>{children}</div>
    </li>
  );
};

const tagToComponent: { [key: string]: any } = {
  h1: [Heading, Styles.heading],
  h2: [Heading, Styles.heading],
  h3: [Heading, Styles.heading],
  h4: [Heading, Styles.heading],
  h5: [Heading, Styles.heading],
  h6: [Heading, Styles.heading],
  p: ['p', Styles.paragraph],
  ol: ['ol', Styles.list],
  ul: ['ul', Styles.list],
  li: [ListItem],
};

const convertHtmlAst = (node: HTMLAST, key: string) => {
  const [Component, styles] = tagToComponent[node.tagName] || [];
  return (
    Component && (
      <Component css={styles} key={key}>
        {node.reactChildren}
      </Component>
    )
  );
};

/**
 * Description of a Zap for a `ZapCard` / `ZapCardDetails` component.
 */
export const ZapCardDetailsHtml = (props: Props) => {
  return (
    <div css={Styles.root}>
      {convertHtmlAstToReactElements(props.htmlAst, convertHtmlAst)}
    </div>
  );
};
